import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequestWithMeta } from "@/helper/api";
import type { CompanyQuickSearchResult } from "@/types/QuickSearch";

export interface CompanyMissingRequiredRow extends CompanyQuickSearchResult {
  parent_name: string | null;
  missing_required_labels: string[];
}

export interface CompaniesMissingRequiredFilters {
  includeTestCompanies: boolean;
  parentsOnly: boolean;
}

export const DEFAULT_COMPANIES_MISSING_REQUIRED_FILTERS: CompaniesMissingRequiredFilters =
  {
    includeTestCompanies: false,
    parentsOnly: false,
  };

export type CompaniesMissingRequiredStatus =
  | "idle"
  | "loading"
  | "done"
  | "error";

interface CompaniesMissingRequiredList {
  status: CompaniesMissingRequiredStatus;
  rows: CompanyMissingRequiredRow[];
}

const IDLE_LIST: CompaniesMissingRequiredList = {
  status: "idle",
  rows: [],
};

const fetchCompaniesMissingRequired = async (
  filters: CompaniesMissingRequiredFilters,
  signal: AbortSignal
): Promise<CompanyMissingRequiredRow[]> => {
  const response = await apiRequestWithMeta<{
    results?: CompanyMissingRequiredRow[];
  }>({
    method: "get",
    entity: "anchorData",
    additionalRouteParts: { companies_missing_required: "/" },
    parametersToRender: {
      include_test_companies: filters.includeTestCompanies,
      parents_only: filters.parentsOnly,
    },
    disableGlobalSpinner: true,
    config: { signal },
  });
  return response.data.results ?? [];
};

export function useCompaniesMissingRequiredAnchorData() {
  const [list, setList] = useState<CompaniesMissingRequiredList>(IDLE_LIST);
  const requestControllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async (filters: CompaniesMissingRequiredFilters) => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setList((current) => ({ ...current, status: "loading" }));

    try {
      const rows = await fetchCompaniesMissingRequired(
        filters,
        controller.signal
      );
      if (controller.signal.aborted) return;

      setList({ status: "done", rows });
    } catch {
      if (!controller.signal.aborted) {
        setList({ ...IDLE_LIST, status: "error" });
      }
    }
  }, []);

  useEffect(() => () => requestControllerRef.current?.abort(), []);

  return { ...list, load };
}
